import { z } from 'zod';
import { BeneficialOwnerInput } from './aml.js';
import { Verification } from './data-states.js';
import { PEP_NOT_STATED, PepStatement } from './pep.js';
import { CountryCode as Country, isoDay as day, isoInstant as instant } from './values.js';
/**
 * What the contracting party of an intake states about itself in the portal (ADR-026): the data
 * § 11 Abs. 4 GwG names for a natural person and for a legal person or partnership. Every field
 * may stay empty - which of them a case needs, the rules of the responsible experts will say
 * (ADR-025 § 8); until then an empty field is "not stated", never "not needed".
 */
const text = (max) => z.string().trim().min(1).max(max).nullable();
/** An address anywhere: postal codes differ by country, so none is checked here. */
export const PostalAddress = z
    .object({
    street: text(200),
    postalCode: text(20),
    city: text(100),
    country: Country.nullable(),
})
    .meta({ id: 'PostalAddress' });
/** § 11 Abs. 4 Nr. 1 GwG: a natural person. */
export const NaturalParty = z
    .object({
    kind: z.literal('natural'),
    givenName: text(100),
    familyName: text(100),
    birthDate: day.nullable(),
    birthPlace: text(100),
    nationality: Country.nullable(),
    address: PostalAddress,
    /** Politically exposed (§ 10 Abs. 1 Nr. 4 GwG, ADR-029); left out: not stated. */
    pep: PepStatement.default(PEP_NOT_STATED),
})
    .meta({ id: 'NaturalParty' });
/** § 11 Abs. 4 Nr. 2 GwG: a legal person or partnership. */
export const EntityParty = z
    .object({
    kind: z.literal('entity'),
    name: text(200),
    legalForm: text(100),
    registerCourt: text(100),
    registerNumber: text(50),
    seat: PostalAddress,
    /** The members of the managing body or the legal representatives, by name. */
    representatives: z.array(z.string().trim().min(1).max(200)).max(20),
})
    .meta({ id: 'EntityParty' });
export const ContractingPartyInput = z
    .discriminatedUnion('kind', [NaturalParty, EntityParty])
    .meta({ id: 'ContractingPartyInput' });
/** Which data an intake asks for: a natural person's for a private client, else an entity's. */
export const partyKindOf = (kind) => kind === 'private' ? 'natural' : 'entity';
/** Who stated it: the contracting party in the portal, the firm, or a register it took over. */
export const PartySource = z.enum(['client', 'firm', 'registry']).meta({ id: 'PartySource' });
/** One statement on the contracting party; a later one replaces it, it stays in the history. */
export const PartyStatement = z
    .object({
    id: z.uuid(),
    data: ContractingPartyInput,
    source: PartySource,
    verification: Verification,
    recordedAt: instant,
    recordedBy: z.string(),
    /** Taken from a register: which data of which date. */
    evidenceRef: z.string().nullable(),
})
    .meta({ id: 'PartyStatement' });
/** The statement in force, and which data the intake asks for. */
export const ContractingParty = z
    .object({ expects: z.enum(['natural', 'entity']), current: PartyStatement.nullable() })
    .meta({ id: 'ContractingParty' });
/** The firm checks what the client stated: a new statement with the same data. */
export const PartyVerificationInput = z
    .object({ verification: Verification })
    .meta({ id: 'PartyVerificationInput' });
/** A beneficial owner as the contracting party states it: its origin and check are set by Kanos. */
export const RespondentOwnerInput = BeneficialOwnerInput.omit({
    source: true,
    verification: true,
    // The firm's persons are not the portal's to name (ADR-051).
    personId: true,
}).meta({ id: 'RespondentOwnerInput' });
//# sourceMappingURL=intake-party.js.map